import { Injectable } from '@angular/core';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SchoolClassService {

    private sharingLink: BehaviorSubject<string> = new BehaviorSubject('');
    sharingLink$: Observable<string> = this.sharingLink.asObservable();

    private _schoolClassListPromise: Promise<any>;
    private _schoolClassListCancelToken: CancelTokenSource;

    constructor(private routeService: RouteService,
                private restService: RestService,
                private applicationStateService: ApplicationStateService) {
    }

    /**
     * Initialize school class service.
     *
     * @returns {Promise<any>}
     */
    init(): void {
        if (this.applicationStateService.isOfflineModeEnabled || this.hasClassesForNotification()) {
            return;
        }

        this.reload();
    }

    /**
     * Determine if user has classes to send notifications.
     *
     * @returns boolean
     */
    hasClassesForNotification(): boolean {
        return this.applicationStateService.schoolClassList.length > 0;
    }

    /**
     * Set sharing link.
     *
     * @param value
     * @returns void
     */
    setSharingLink(value: string): void {
        this.sharingLink.next(value);
    }

    /**
     * Reload school class and persist in app state.
     *
     * @returns {Promise<any>}
     */
    async reload(): Promise<any> {
        if (this._schoolClassListCancelToken) {
            this._schoolClassListCancelToken.cancel('Reload class list');
        }

        try {
            const result = await this.fetchClassList();
            this.applicationStateService.schoolClassList = result.data;
        } catch (error) {
            this.applicationStateService.schoolClassList = [];
        }
    }

    /**
     * Get class list of current user.
     *
     * @return {Promise<AxiosResponse>}
     */
    async fetchClassList(): Promise<AxiosResponse> {
        this._schoolClassListCancelToken = axios.CancelToken.source();
        this._schoolClassListPromise = axios.get(this.getClassListUrl(), {
            cancelToken: this._schoolClassListCancelToken.token
        });

        return this._schoolClassListPromise;
    }

    /**
     * Notify class with given texts.
     *
     * @param classId  {number}
     * @param title  {string}
     * @param text {string}
     *
     * @return {Promise<any>}
     */
    async notifyClass(classId: number, title: string, text: string): Promise<any> {
        return this.restService.post(this.getNotificationUrl(classId), {
            title,
            text
        });
    }

    /**
     * Get url for class list of current user
     */
    getClassListUrl(): string {
        return this.routeService.getRelativeRoute(environment.endpointUrls.schoolClassList, []);
    }

    /**
     * Get url to notify school class
     * @param classId
     */
    getNotificationUrl(classId: number): string {
        return this.routeService.getRelativeRoute(environment.endpointUrls.schoolClassNotify, {
            classId
        });
    }
}
